<script setup lang="ts">
import { Check, GripVertical, Minus, X } from 'lucide-vue-next'
import { computed } from 'vue'

import AppSortableList from '@/components/AppSortableList.vue'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { type RegistryEntity, type ReportSort, allFields } from '@/types/reports'

const props = defineProps<{
  columns: string[]
  entity: RegistryEntity
  sort: ReportSort | null
}>()

const emit = defineEmits<{
  'update:columns': [string[]]
  'update:sort': [ReportSort | null]
}>()

const fieldOptions = computed(() => allFields(props.entity))

const fieldLabels = computed(() => new Map(fieldOptions.value.map((f) => [f.path, f.label])))

function fieldLabel(path: string): string {
  return fieldLabels.value.get(path) ?? path
}

interface ColumnGroup {
  label: string
  paths: string[]
}

const columnGroups = computed<ColumnGroup[]>(() => [
  { label: props.entity.label, paths: props.entity.fields.map((f) => f.key) },
  ...Object.entries(props.entity.relations).map(([relKey, rel]) => ({
    label: rel.label,
    paths: rel.fields.map((f) => `${relKey}.${f.key}`),
  })),
])

function groupState(paths: string[]): boolean | 'indeterminate' {
  const selectedCount = paths.filter((p) => props.columns.includes(p)).length
  if (selectedCount === 0) return false
  if (selectedCount === paths.length) return true
  return 'indeterminate'
}

function toggle(path: string, checked: boolean) {
  const next = checked ? [...props.columns, path] : props.columns.filter((c) => c !== path)
  emit('update:columns', next)

  if (!checked && props.sort?.field === path) emit('update:sort', null)
}

function toggleGroup(paths: string[], checked: boolean) {
  const next = checked
    ? [...props.columns, ...paths.filter((p) => !props.columns.includes(p))]
    : props.columns.filter((c) => !paths.includes(c))
  emit('update:columns', next)

  if (!checked && props.sort && paths.includes(props.sort.field)) emit('update:sort', null)
}

function setSortField(field: string) {
  emit('update:sort', { field, direction: props.sort?.direction ?? 'asc' })
}

function setSortDirection(direction: 'asc' | 'desc') {
  if (!props.sort) return
  emit('update:sort', { ...props.sort, direction })
}
</script>

<template>
  <div class="space-y-4">
    <div>
      <p class="mb-2 text-sm font-medium">Display columns</p>
      <div class="flex flex-wrap gap-x-16 gap-y-4">
        <div v-for="group in columnGroups" :key="group.label">
          <label class="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
            <Checkbox
              :model-value="groupState(group.paths)"
              @update:model-value="(checked) => toggleGroup(group.paths, !!checked)"
            >
              <Minus v-if="groupState(group.paths) === 'indeterminate'" class="size-3.5" />
              <Check v-else class="size-3.5" />
            </Checkbox>
            {{ group.label }}
          </label>
          <div class="ml-6 space-y-1.5">
            <label v-for="path in group.paths" :key="path" class="flex items-center gap-1.5 text-sm">
              <Checkbox
                :model-value="columns.includes(path)"
                @update:model-value="(checked) => toggle(path, !!checked)"
              />
              {{ fieldLabel(path) }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <div v-if="columns.length > 0">
      <p class="mb-2 text-sm font-medium">
        Selected columns <span class="text-muted-foreground font-normal">— drag to reorder</span>
      </p>
      <AppSortableList
        :model-value="columns"
        :item-key="(path: string) => path"
        class="flex flex-wrap gap-2"
        @update:model-value="(next) => emit('update:columns', next)"
      >
        <template #default="{ item: path }">
          <div class="bg-muted/50 flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm shadow-xs select-none">
            <GripVertical class="text-muted-foreground size-3.5" />
            {{ fieldLabel(path) }}
            <button
              type="button"
              class="text-muted-foreground hover:text-destructive"
              :aria-label="`Remove ${fieldLabel(path)}`"
              @click="toggle(path, false)"
            >
              <X class="size-3.5" />
            </button>
          </div>
        </template>
      </AppSortableList>
    </div>

    <p v-else class="text-muted-foreground text-sm italic">
      No columns selected — all fields on {{ entity.label }} will be shown by default.
    </p>

    <div class="flex items-center gap-2">
      <p class="text-sm font-medium">Sort by</p>
      <Select :model-value="sort?.field ?? undefined" @update:model-value="(v) => setSortField(v as string)">
        <SelectTrigger class="w-48">
          <SelectValue placeholder="Default (newest first)" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="f in fieldOptions" :key="f.path" :value="f.path">{{ f.label }}</SelectItem>
        </SelectContent>
      </Select>
      <Select
        v-if="sort"
        :model-value="sort.direction"
        @update:model-value="(v) => setSortDirection(v as 'asc' | 'desc')"
      >
        <SelectTrigger class="w-32">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="asc">Ascending</SelectItem>
          <SelectItem value="desc">Descending</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
</template>
